import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  Fab,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CompressIcon from '@mui/icons-material/Compress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import { statusColors, calendarColors } from '@/styles/theme';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { format } from 'date-fns';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCalendarStatusOptions } from '@/features/calendar/useCalendarStatusOptions';
import { useCalendarFiltersStore } from '@/stores/calendarFiltersStore';
import type { Mechanic, BookingStatus } from '@/types';
import type { DayLayout } from '@/stores/calendarStore';
import type { CalendarDensity, MechanicGroup } from '@/features/calendar/calendarMechanicPrefs';
import { MechanicGroupsControl } from './MechanicGroupsControl';
import { dateFnsLocale } from '@/utils/locale';

export type CalendarView = 'day' | 'week' | 'month';

// Short day-view heading (#4182): "su 22.11. 2026" rather than the full weekday
// and month name, so the navigation row does not wrap.
const DAY_LABEL_FORMATS: Record<string, string> = {
  fi: 'EEEEEE d.M. yyyy',
  sv: 'EEEEEE d/M yyyy',
};
const DAY_LABEL_FORMAT_DEFAULT = 'EEE MMM d, yyyy';

interface CalendarToolbarProps {
  currentDate: Date;
  view: CalendarView;
  dayLayout: DayLayout;
  showExtendedHours: boolean;
  compressClosedHours: boolean;
  mechanics: Mechanic[];
  serviceTypes: string[];
  selectedMechanicIds: string[];
  selectedStatuses: BookingStatus[];
  selectedServiceTypes: string[];
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onDateChange: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onDayLayoutChange: (layout: DayLayout) => void;
  onShowExtendedHoursChange: (show: boolean) => void;
  onCompressClosedHoursChange: (compress: boolean) => void;
  onMechanicChange: (ids: string[]) => void;
  onStatusChange: (statuses: BookingStatus[]) => void;
  onServiceTypeChange: (types: string[]) => void;
  onNewWorkOrder?: () => void;
  density?: CalendarDensity;
  onDensityChange?: (density: CalendarDensity) => void;
  mechanicGroups?: MechanicGroup[];
  onSaveMechanicGroup?: (name: string) => void;
  onApplyMechanicGroup?: (id: string) => void;
  onDeleteMechanicGroup?: (id: string) => void;
}

export function CalendarToolbar({
  currentDate,
  view,
  dayLayout,
  showExtendedHours,
  compressClosedHours,
  mechanics,
  serviceTypes,
  selectedMechanicIds,
  selectedStatuses,
  selectedServiceTypes,
  onPrevious,
  onNext,
  onToday,
  onDateChange,
  onViewChange,
  onDayLayoutChange,
  onShowExtendedHoursChange,
  onCompressClosedHoursChange,
  onMechanicChange,
  onStatusChange,
  onServiceTypeChange,
  onNewWorkOrder,
  density = 'comfortable',
  onDensityChange,
  mechanicGroups = [],
  onSaveMechanicGroup,
  onApplyMechanicGroup,
  onDeleteMechanicGroup,
}: CalendarToolbarProps) {
  const { t } = useTranslation('calendar');
  const isMobile = useIsMobile();
  const filtersVisible = useCalendarFiltersStore((s) => s.filtersVisible);
  const toggleFiltersVisible = useCalendarFiltersStore((s) => s.toggleFiltersVisible);
  const miniCalendarVisible = useCalendarFiltersStore((s) => s.miniCalendarVisible);
  const toggleMiniCalendarVisible = useCalendarFiltersStore((s) => s.toggleMiniCalendarVisible);
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [moreMechanicsAnchor, setMoreMechanicsAnchor] = useState<HTMLElement | null>(null);
  const [legendAnchor, setLegendAnchor] = useState<HTMLElement | null>(null);

  const selectedMechanics = useMemo(
    () => mechanics.filter((m) => selectedMechanicIds.includes(m.id)),
    [mechanics, selectedMechanicIds],
  );

  const statusOptions = useCalendarStatusOptions();

  const handleOpenDatePicker = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(event.currentTarget);
  }, []);

  const handleCloseDatePicker = useCallback(() => {
    setDatePickerAnchor(null);
  }, []);

  const handleDateSelect = useCallback((newDate: Date | null) => {
    if (newDate) {
      onDateChange(newDate);
      handleCloseDatePicker();
    }
  }, [onDateChange, handleCloseDatePicker]);

  const getDateLabel = () => {
    switch (view) {
      case 'day': {
        const locale = dateFnsLocale();
        return format(currentDate, DAY_LABEL_FORMATS[locale.code] ?? DAY_LABEL_FORMAT_DEFAULT, { locale });
      }
      case 'week':
        return `${t('weekLabel')} ${format(currentDate, 'w, MMMM yyyy', { locale: dateFnsLocale() })}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: dateFnsLocale() });
    }
  };

  // Month names are lowercase in Finnish and Swedish; the short day heading
  // keeps its lowercase weekday ("su 22.11. 2026").
  const labelTransform = view === 'day' ? 'none' : 'capitalize';

  // Layout toggle (shown only in day view). Renders as a compact ToggleButtonGroup
  // with icons for horizontal (TimelineCalendar) vs vertical (DayCalendar).
  const dayLayoutToggle = (
    <ToggleButtonGroup
      value={dayLayout}
      exclusive
      size="small"
      onChange={(_, v: DayLayout | null) => {
        if (v) onDayLayoutChange(v);
      }}
      aria-label={t('dayLayout')}
    >
      <ToggleButton value="horizontal" aria-label={t('dayLayoutHorizontal')}>
        <Tooltip title={t('dayLayoutHorizontal')}>
          <ViewWeekIcon fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="vertical" aria-label={t('dayLayoutVertical')}>
        <Tooltip title={t('dayLayoutVertical')}>
          <ViewAgendaIcon fontSize="small" />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // Compress the closed parts of the time axis (#1313). Only the horizontal
  // TimelineCalendar takes the prop; the vertical DayCalendar derives its axis
  // from the opening hours already, so there is nothing to compress there.
  // Icon is Compress rotated 90° so arrows push along the time axis (horizontal),
  // not the mechanic rows (vertical) — the default glyph reads as row density.
  const showsHorizontalTimeline = !(view === 'day' && dayLayout === 'vertical');
  // Icon-only toolbar buttons (#4182): the label lives in the tooltip and aria-label.
  const iconButtonSx = { minWidth: 0, px: 1 } as const;
  const compressToggle = showsHorizontalTimeline ? (
    <Tooltip title={t('compressClosedHoursHint')}>
      <Button
        variant={compressClosedHours ? 'contained' : 'outlined'}
        size="small"
        onClick={() => { onCompressClosedHoursChange(!compressClosedHours); }}
        aria-label={t('compressClosedHours')}
        aria-pressed={compressClosedHours}
        sx={iconButtonSx}
      >
        <CompressIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
      </Button>
    </Tooltip>
  ) : null;

  const extendedHoursToggle = (
    <Tooltip title={t('showExtendedHours')}>
      <Button
        variant={showExtendedHours ? 'contained' : 'outlined'}
        size="small"
        onClick={() => { onShowExtendedHoursChange(!showExtendedHours); }}
        aria-label={t('showExtendedHours')}
        aria-pressed={showExtendedHours}
        sx={iconButtonSx}
      >
        <ScheduleIcon fontSize="small" />
      </Button>
    </Tooltip>
  );

  const densityToggle = showsHorizontalTimeline && onDensityChange ? (
    <ToggleButtonGroup
      value={density}
      exclusive
      size="small"
      onChange={(_, v: CalendarDensity | null) => {
        if (v) onDensityChange(v);
      }}
      aria-label={t('density.label')}
    >
      <ToggleButton value="comfortable" aria-label={t('density.comfortable')}>
        <Tooltip title={t('density.comfortableHint')}>
          <DensityMediumIcon fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="compact" aria-label={t('density.compact')}>
        <Tooltip title={t('density.compactHint')}>
          <DensitySmallIcon fontSize="small" />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  ) : null;

  const groupsControl = onSaveMechanicGroup && onApplyMechanicGroup && onDeleteMechanicGroup ? (
    <MechanicGroupsControl
      groups={mechanicGroups}
      selectedMechanicIds={selectedMechanicIds}
      onSave={onSaveMechanicGroup}
      onApply={onApplyMechanicGroup}
      onDelete={onDeleteMechanicGroup}
    />
  ) : null;

  // Count active filters — empty selection means "no filter" for all three
  const activeFilterCount =
    (selectedMechanicIds.length > 0 ? 1 : 0) +
    (selectedStatuses.length > 0 ? 1 : 0) +
    (selectedServiceTypes.length > 0 ? 1 : 0);

  // Toggle button that reveals the filter row. Shows a small badge with the
  // active filter count so users still see "filters are active" when the row
  // is collapsed.
  const filterToggleButton = (
    <Tooltip title={t('filters')}>
      <Badge
        badgeContent={activeFilterCount}
        color="primary"
        overlap="circular"
        sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, padding: '0 4px' } }}
      >
        <IconButton
          onClick={toggleFiltersVisible}
          color={filtersVisible ? 'primary' : 'default'}
          aria-label={t('filters')}
          aria-pressed={filtersVisible}
          size={isMobile ? 'medium' : 'small'}
          sx={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}
        >
          <FilterListIcon />
        </IconButton>
      </Badge>
    </Tooltip>
  );

  const filterControls = (
    <>
      {/* Mechanic filter — multi-select Autocomplete with compact chip rendering.
          Dropdown stays open while selecting (disableCloseOnSelect); closes on
          outside click or Escape. Only the first 4 chips render inline; the rest
          collapse into a "+ N muuta" chip that opens a popover with all names. */}
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={mechanics}
        getOptionLabel={(m) => `${m.firstName} ${m.lastName}`}
        value={selectedMechanics}
        onChange={(_, vals) => { onMechanicChange(vals.map((v) => v.id)); }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderTags={(value, getTagProps) => {
          const maxVisible = 4;
          const visible = value.slice(0, maxVisible);
          const hiddenCount = value.length - maxVisible;
          return (
            <>
              {visible.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    size="small"
                    label={`${option.firstName} ${option.lastName}`}
                  />
                );
              })}
              {hiddenCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('moreCount', { count: hiddenCount })}
                  aria-label={t('showAllSelectedMechanics')}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoreMechanicsAnchor(e.currentTarget);
                  }}
                />
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label={t('mechanic')} />
        )}
        sx={{ minWidth: { xs: 160, md: 250 } }}
      />
      {groupsControl}

      {/* Status filter — multi-select Autocomplete */}
      <Autocomplete
        multiple
        size="small"
        options={statusOptions}
        getOptionLabel={(key) => t(`statusLabels.${key}`)}
        value={selectedStatuses}
        onChange={(_, vals) => { onStatusChange(vals); }}
        renderInput={(params) => (
          <TextField {...params} label={t('status')} />
        )}
        sx={{ minWidth: { xs: 160, md: 250 } }}
      />

      {/* Service type filter — multi-select Autocomplete */}
      <Autocomplete
        multiple
        size="small"
        options={serviceTypes}
        value={selectedServiceTypes}
        onChange={(_, vals) => { onServiceTypeChange(vals); }}
        renderInput={(params) => (
          <TextField {...params} label={t('serviceType')} />
        )}
        sx={{ minWidth: { xs: 160, md: 250 } }}
      />
    </>
  );

  // Item-style legend (#1139): explains how exact appointments, floating
  // (during-the-day) work, tentative and paused entries are drawn.
  const legendSwatch = { width: 28, height: 16, borderRadius: 0.5, flexShrink: 0 } as const;
  const legendRows: { key: string; swatch: React.ReactNode }[] = [
    {
      key: 'exact',
      swatch: (
        <Box
          sx={{
            ...legendSwatch,
            backgroundColor: statusColors.pending,
            border: '2px solid rgba(255,255,255,0.85)',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AccessTimeFilledIcon sx={{ fontSize: 10, color: 'common.white' }} />
        </Box>
      ),
    },
    {
      key: 'floating',
      swatch: (
        <Box sx={{ ...legendSwatch, backgroundColor: statusColors.pending, border: `1px solid ${calendarColors.itemBorder}` }} />
      ),
    },
    {
      key: 'tentative',
      swatch: (
        <Box
          sx={{
            ...legendSwatch,
            backgroundColor: `${calendarColors.unassigned}30`,
            border: `2px dashed ${calendarColors.tentative}`,
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(217,151,31,0.15) 4px, rgba(217,151,31,0.15) 8px)',
          }}
        />
      ),
    },
    {
      key: 'paused',
      swatch: (
        <Box
          sx={{
            ...legendSwatch,
            backgroundColor: statusColors.paused,
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.25) 5px, rgba(0,0,0,0.25) 10px)',
          }}
        />
      ),
    },
  ];
  const legendControl = (
    <>
      <Tooltip title={t('legend.title')}>
        <IconButton
          size="small"
          onClick={(e) => { setLegendAnchor(e.currentTarget); }}
          aria-label={t('legend.title')}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(legendAnchor)}
        anchorEl={legendAnchor}
        onClose={() => { setLegendAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 300 }}>
          <Typography variant="subtitle2">{t('legend.title')}</Typography>
          {legendRows.map((row) => (
            <Box key={row.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {row.swatch}
              <Typography variant="caption">{t(`legend.${row.key}`)}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );

  if (isMobile) {
    return (
      <Box sx={{ mb: 1 }}>
        {/* Row 1: Date navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Tooltip title={t('previous')}>
            <IconButton onClick={onPrevious} size="small" aria-label={t('previous')} sx={{ minWidth: 44, minHeight: 44 }}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('today')}>
            <IconButton onClick={onToday} size="small" aria-label={t('today')} sx={{ minWidth: 44, minHeight: 44 }}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('next')}>
            <IconButton onClick={onNext} size="small" aria-label={t('next')} sx={{ minWidth: 44, minHeight: 44 }}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{ ml: 0.5, textTransform: labelTransform, fontWeight: 600, flex: 1, minWidth: 0 }}
            noWrap
          >
            {getDateLabel()}
          </Typography>
          <Tooltip title={t('selectDate')}>
            <IconButton
              onClick={handleOpenDatePicker}
              size="small"
              aria-label={t('selectDate')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <CalendarMonthIcon />
            </IconButton>
          </Tooltip>
          {legendControl}
        </Box>

        {/* Row 2: View toggle + filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" sx={{ flex: 1 }}>
            <Button
              variant={view === 'day' ? 'contained' : 'outlined'}
              onClick={() => { onViewChange('day'); }}
              sx={{ flex: 1 }}
            >
              {t('day')}
            </Button>
            <Button
              variant={view === 'week' ? 'contained' : 'outlined'}
              onClick={() => { onViewChange('week'); }}
              sx={{ flex: 1 }}
            >
              {t('week')}
            </Button>
            <Button
              variant={view === 'month' ? 'contained' : 'outlined'}
              onClick={() => { onViewChange('month'); }}
              sx={{ flex: 1 }}
            >
              {t('month')}
            </Button>
          </ButtonGroup>

          {filterToggleButton}
        </Box>

        {/* Day layout + extended hours (day view only) and the axis-compression
            toggle, which applies to every horizontal view */}
        {(view === 'day' || compressToggle || densityToggle) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {compressToggle}
            {densityToggle}
            {view === 'day' && (
              <>
                {extendedHoursToggle}
                {dayLayoutToggle}
              </>
            )}
          </Box>
        )}

        {/* Collapsible filters */}
        <Collapse in={filtersVisible}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {filterControls}
          </Box>
        </Collapse>

        {/* FAB for new work order */}
        {onNewWorkOrder && (
          <Fab
            color="primary"
            size="medium"
            onClick={onNewWorkOrder}
            sx={{ position: 'fixed', bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))', right: 16, zIndex: 1050 }}
            aria-label={t('newWorkOrder')}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Date Picker Popover */}
        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleCloseDatePicker}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <DateCalendar
            value={currentDate}
            onChange={handleDateSelect}
          />
        </Popover>

        {/* Popover listing all selected mechanic names */}
        <Popover
          open={Boolean(moreMechanicsAnchor)}
          anchorEl={moreMechanicsAnchor}
          onClose={() => { setMoreMechanicsAnchor(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, maxWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('selectedMechanics', { count: selectedMechanics.length })}
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {selectedMechanics.map((m) => (
                <Chip
                  key={m.id}
                  size="small"
                  label={`${m.firstName} ${m.lastName}`}
                />
              ))}
            </Stack>
          </Box>
        </Popover>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Top row: Navigation + View toggle + actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        {/* Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('previous')}>
            <IconButton onClick={onPrevious} size="small" aria-label={t('previous')}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TodayIcon />}
            onClick={onToday}
          >
            {t('today')}
          </Button>
          <Tooltip title={t('next')}>
            <IconButton onClick={onNext} size="small" aria-label={t('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Typography
              variant="h6"
              sx={{ textTransform: labelTransform, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {getDateLabel()}
            </Typography>
            <Tooltip title={t('selectDate')}>
              <IconButton
                onClick={handleOpenDatePicker}
                size="small"
                aria-label={t('selectDate')}
              >
                <CalendarMonthIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* View toggle + new booking */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small">
            <Tooltip title={t('day')}>
              <Button
                variant={view === 'day' ? 'contained' : 'outlined'}
                onClick={() => { onViewChange('day'); }}
                aria-label={t('day')}
              >
                {t('dayShort')}
              </Button>
            </Tooltip>
            <Tooltip title={t('week')}>
              <Button
                variant={view === 'week' ? 'contained' : 'outlined'}
                onClick={() => { onViewChange('week'); }}
                aria-label={t('week')}
              >
                {t('weekShort')}
              </Button>
            </Tooltip>
            <Tooltip title={t('month')}>
              <Button
                variant={view === 'month' ? 'contained' : 'outlined'}
                onClick={() => { onViewChange('month'); }}
                aria-label={t('month')}
              >
                {t('monthShort')}
              </Button>
            </Tooltip>
          </ButtonGroup>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {legendControl}
            {compressToggle}
            {densityToggle}
            {view === 'day' && (
              <>
                {extendedHoursToggle}
                {dayLayoutToggle}
                <Tooltip title={t('miniCalendar')}>
                  <IconButton
                    size="small"
                    onClick={toggleMiniCalendarVisible}
                    color={miniCalendarVisible ? 'primary' : 'default'}
                    aria-label={t('miniCalendar')}
                    aria-pressed={miniCalendarVisible}
                  >
                    <CalendarViewMonthIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          {onNewWorkOrder && (
            <Tooltip title={t('newWorkOrder')}>
              <Button
                variant="contained"
                size="small"
                onClick={onNewWorkOrder}
                aria-label={t('newWorkOrder')}
                sx={iconButtonSx}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}

          {filterToggleButton}
        </Box>
      </Box>

      {/* Filters row — collapsed by default, toggled via the button above */}
      <Collapse in={filtersVisible}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          {filterControls}
        </Box>
      </Collapse>

      {/* Date Picker Popover */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleCloseDatePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <DateCalendar
          value={currentDate}
          onChange={handleDateSelect}
        />
      </Popover>

      {/* Popover listing all selected mechanic names */}
      <Popover
        open={Boolean(moreMechanicsAnchor)}
        anchorEl={moreMechanicsAnchor}
        onClose={() => { setMoreMechanicsAnchor(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('selectedMechanics', { count: selectedMechanics.length })}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {selectedMechanics.map((m) => (
              <Chip
                key={m.id}
                size="small"
                label={`${m.firstName} ${m.lastName}`}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
